/**
 * src/hooks/useWebShortcuts.ts — global keyboard shortcuts (W5 web ergonomics).
 *
 * Web-only. Single-key navigation accelerators for desktop users. Ignored while typing in an
 * input/textarea/contenteditable, and when a modifier (Cmd/Ctrl/Alt) is held (so it never
 * clobbers browser/OS shortcuts). No-op on native.
 *
 *   h → Home   q → Queue   c → Capture   n → New estimate   m → More hub
 *
 * NEVER imports @smartbox/* (mobile invariant 1).
 */
import { useEffect } from 'react';
import { Platform } from 'react-native';
import { useRouter } from 'expo-router';

const ROUTES: Record<string, string> = {
  h: '/(app)/(tabs)/home',
  q: '/(app)/(tabs)/queue',
  c: '/(app)/capture',
  n: '/(app)/estimates/new',
  m: '/(app)/more',
};

export function useWebShortcuts(): void {
  const router = useRouter();

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable) return;
      }
      const dest = ROUTES[e.key.toLowerCase()];
      if (!dest) return;
      e.preventDefault();
      router.push(dest as never);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [router]);
}
